import { action, computed, makeObservable, observable } from 'mobx'
import type { MeetingCubit } from '../../MeetingCubit'
import {
  ExhibitGridDisplayMode,
  isProcessingSlideType,
  SlideType,
} from '../../../firestore/Slide/types'
import type { SlideModel } from '../../../models/SlideModel'
import type { Media } from '../../../models/Media'
import { fetchMedia } from '../../../firestore/Media'
import { SlideQuestionType } from '../../../models/SlideQuestionType'
import type { MeetingQuestion } from '../MeetingQuestion'

export class Slide {
  id: string
  name: string
  index: number
  duration: number
  slideVideoDuration?: number
  slideVideoURL: string | null = null
  slideVideoYouTubeURL: string | null = null
  slideVideoStreamingURL: string | null = null
  slideImageURL: string | null = null
  slideImageAltText: string | null = null
  slideIframeURL: string | null = null
  slideMarkdown: string | null = null
  slideGoogleTemplateURL: string | null = null
  description: string
  type: SlideType
  order: number
  meeting: MeetingCubit
  mediaId: string | null
  // BL-4216 — Dynamic Intro Video slide: two Media references chosen at
  // runtime based on MeetingCubit.wasLaunchedAsSolo.
  slideGroupMediaId: string | null = null
  slideSoloMediaId: string | null = null
  media: Media | null
  isSkippable: boolean = false
  inputQuestionIds: string[]

  animatedCardsTitle?: string
  animatedCardsDescription?: string
  animatedCardsItems?: { title: string; description?: string }[]
  animatedCardsSubtext?: string

  interactiveFormSchema?: string
  exhibitDisplayMode: ExhibitGridDisplayMode

  learningObjectivesHeading?: string
  learningObjectivesIntro?: string
  learningObjectivesBullets?: string[]

  constructor(meeting: MeetingCubit, model: SlideModel, index: number) {
    this.meeting = meeting
    const data = model.data
    this.id = model.id
    this.index = index
    this.name = data.slideName
    this.duration = data.slideDuration
    this.description = data.slideDescription
    this.type = data.slideType
    this.order = data.slideOrder || 0
    this.slideVideoDuration = data.slideVideoDuration
    this.slideVideoStreamingURL = data.slideVideoStreamingURL || null
    this.slideVideoURL = data.slideVideoURL || null
    this.slideVideoYouTubeURL = data.slideVideoYouTubeURL || null
    this.slideImageURL = data.slideImageURL || null
    this.slideIframeURL = data.slideIframeURL || null
    this.slideImageAltText = data.slideImageAltText || null
    this.slideMarkdown = data.slideMarkdown || null
    this.slideGoogleTemplateURL = data.slideGoogleTemplateURL || null
    this.mediaId = data.mediaId || null
    this.slideGroupMediaId = data.slideGroupMediaId || null
    this.slideSoloMediaId = data.slideSoloMediaId || null
    this.media = null
    this.isSkippable = data.isSkippable || false
    this.inputQuestionIds = data.slideInputQuestionIds || []
    this.exhibitDisplayMode =
      data.slideExhibitGridDisplayMode ?? ExhibitGridDisplayMode.upToNow

    this.interactiveFormSchema = data.schema || undefined

    this.animatedCardsTitle = data.slideAnimatedCardsTitle || undefined
    this.animatedCardsDescription =
      data.slideAnimatedCardsDescription || undefined
    this.animatedCardsItems = data.slideAnimatedCardsItems || undefined
    this.animatedCardsSubtext = data.slideAnimatedCardsSubtext || undefined

    this.learningObjectivesHeading =
      data.slideLearningObjectivesHeading || undefined
    this.learningObjectivesIntro =
      data.slideLearningObjectivesIntro || undefined
    this.learningObjectivesBullets =
      data.slideLearningObjectivesBullets || undefined

    makeObservable(this, {
      duration: true,
      media: observable,
      mediaId: observable,
      titleIndex: computed,
      hasMedia: computed,
      updateDuration: action,
      setMedia: action,
      sortableQuestion: computed,
      moderationTasks: computed,
      sortableQuestions: computed,
      popUpInstructions: computed,
      heading: computed,
      slideQuestions: computed,
      hasSuggestedTimer: computed,
      learningObjectivesHeading: observable,
      learningObjectivesIntro: observable,
      learningObjectivesBullets: observable,
    })

    this.afterConstructor()
  }

  // This is used un subclasses to not need to super() the constructor
  afterConstructor() {}

  get hasMedia() {
    return !!this.mediaId
  }

  async loadMedia() {
    if (this.mediaId) {
      const media = await fetchMedia(this.meeting.repository, {
        mediaId: this.mediaId,
      })

      this.setMedia(media)
    }
  }

  // BL-4216 — load a Media doc by an arbitrary id. Used by the Dynamic Intro
  // Video runtime renderer which picks one of two mediaIds at play time and
  // can't pre-set `this.mediaId` ahead of `loadMedia`.
  async loadMediaById(mediaId: string): Promise<Media> {
    const media = await fetchMedia(this.meeting.repository, { mediaId })
    return media
  }

  setMedia(media: Media) {
    this.media = media
  }

  get durationInMinutes() {
    let duration = 0
    if (this.slideVideoDuration) {
      duration = Math.ceil(this.slideVideoDuration / 60)
    } else {
      duration = Math.ceil(this.duration / 60)
    }
    return duration
  }

  updateDuration(duration: number) {
    this.duration = duration
  }

  get isTimedSlide(): boolean {
    if (this.slideVideoDuration && this.isVideoSlide) return true
    // Dynamic Intro Video has no slideVideoDuration/duration — its length lives
    // on the chosen media (populated by the renderer via setMedia before
    // playback). Treat it as timed so Next disables while it plays.
    if (
      this.type === SlideType.dynamicIntroVideo &&
      (this.media?.data?.mediaVideoDuration ?? 0) > 0
    )
      return true
    if (this.hasSuggestedTimer) return true
    return this.duration > 0
  }

  get hasSuggestedTimer(): boolean {
    return !!this.slideQuestions.find(
      (q) => q.questionType === SlideQuestionType.suggestedTimer
    )
  }

  get titleIndex(): number {
    return 0
  }

  get hasMarkdown(): boolean {
    return !!this.slideMarkdown
  }

  get hasImage(): boolean {
    return !!this.slideImageURL && !this.hasMarkdown // Markdown trumps images
  }

  get altText(): string {
    return this.media?.data.mediaAltText || this.slideImageAltText || this.name
  }

  get isVideoSlide() {
    return (
      this.type === SlideType.video ||
      this.type === SlideType.dynamicIntroVideo ||
      (isProcessingSlideType(this.type) && !this.hasImage)
    )
  }

  get isYouTubeSlide() {
    return this.type === SlideType.video && !!this.slideVideoYouTubeURL
  }

  /**
   * Gets the shared Google document URL for this slide from the room state's slide metadata.
   * This URL is populated when the room starts if this is a sharedGoogleDoc slide type.
   */
  get sharedDocumentUrl(): string | null {
    const slideMetadata = this.meeting.roomState.data.slideMetadata
    if (!slideMetadata) return null
    const metadata = slideMetadata[this.id]
    if (!metadata) return null
    return metadata.sharedDocumentUrl || null
  }

  get slideQuestions(): MeetingQuestion[] {
    return this.meeting.questions.filter((q) => {
      return q.slideId === this.id
    })
  }

  get sortableQuestion() {
    return this.sortableQuestions[0]
  }

  get sortableQuestions(): MeetingQuestion[] {
    return this.meeting.questions.filter((q) => {
      return (
        q.slideId === this.id &&
        q.questionType === SlideQuestionType.sortableStringList
      )
    })
  }

  get popUpInstructions() {
    const popUpInstructions = this.meeting.slideDeckQuestions.models
      .filter((q) => q.data.slideId === this.id)
      .filter(
        (q) => q.data.questionType === SlideQuestionType.popUpInstructions
      )
    return popUpInstructions.at(0) || null
  }

  get moderationTasks() {
    return this.meeting.slideDeckQuestions.models
      .filter((q) => q.data.slideId === this.id)
      .filter((q) => q.data.questionType === SlideQuestionType.moderationTask)
      .sort((a, b) => (a.data.order || 0) - (b.data.order || 0))
  }

  get heading() {
    let heading = ''

    if (
      this.meeting.currentSlideTitles.length > 1 &&
      this.meeting.slideProgressIsComplete
    ) {
      const slideTitle = this.meeting.currentSlideTitles[1]
      // by trimming these we can affect order of the titles with a preceding space
      heading = slideTitle.question.trim()
    } else if (this.meeting.currentSlideTitles.length > 0) {
      const slideTitle = this.meeting.currentSlideTitles[0]
      heading = slideTitle.question.trim()
    }

    return heading
  }
}
